import {
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import {
  ArrowUp,
  Bell,
  BarChart3,
  Banknote,
  BookOpen,
  Boxes,
  Building2,
  BriefcaseBusiness,
  Calculator,
  CalendarDays,
  CheckCircle2,
  CheckSquare,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  ClipboardCheck,
  PackageOpen,
  ScanLine,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageCircle,
  Moon,
  MoreHorizontal,
  Package,
  Receipt,
  Search,
  Settings,
  Shield,
  ShoppingCart,
  Truck,
  Sun,
  Target,
  UserCircle,
  Users,
  X,
} from "lucide-react";
import { playSound } from "../lib/sounds";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Layout() {
  const { user, logout, dark, setDark, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [unread, setUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [orderMenuOpen, setOrderMenuOpen] = useState(
    window.location.pathname.startsWith("/siparis-talep")
  );
  const [taskMenuOpen, setTaskMenuOpen] = useState(
    window.location.pathname.startsWith("/gorevler") || window.location.pathname.startsWith("/tamamlanan") || window.location.pathname.startsWith("/gorev-raporlari")
  );
  const [profitMenuOpen, setProfitMenuOpen] = useState(
    window.location.pathname.startsWith("/eticaret-kar")
  );
  const [warehouseMenuOpen, setWarehouseMenuOpen] = useState(
    window.location.pathname.startsWith("/depo")
  );
  const [b2bMenuOpen, setB2bMenuOpen] = useState(
    window.location.pathname.startsWith("/b2b")
  );
  const [marketplaceMenuOpen, setMarketplaceMenuOpen] = useState(
    window.location.pathname.startsWith("/pazaryeri-fiyat")
  );
  const [documentMenuOpen, setDocumentMenuOpen] = useState(
    window.location.pathname.startsWith("/document")
  );
  const [employeeMenuOpen, setEmployeeMenuOpen] = useState(
    window.location.pathname.startsWith("/calisanlar")
  );

  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileOrderOpen, setMobileOrderOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [scrollUpVisible, setScrollUpVisible] = useState(false);
  const scrollUpHideTimer = useRef(null);
  const lastScrollY = useRef(0);

  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    return localStorage.getItem("ofisgorev-sidebar-collapsed") === "1";
  });

  const prevNotif = useRef(null);
  const prevChat = useRef(null);

  const nav = [
    ...(hasPermission("dashboard") ? [{ to: "/", label: "Panel", icon: LayoutDashboard, end: true }] : []),
    ...(hasPermission("notifications") ? [{ to: "/bildirimler", label: "Bildirimler", icon: Bell, badge: unread }] : []),
  ];

  useEffect(() => {
    const load = async () => {
      try {
        const [notificationsResponse, chatResponse] = await Promise.all([
          api.get("/notifications"),
          api.get("/chat/unread"),
        ]);

        const unreadNotifications = notificationsResponse.data.filter(
          (item) => !item.read
        ).length;

        const unreadChat = chatResponse.data.total || 0;

        const notificationIncreased =
          prevNotif.current !== null &&
          unreadNotifications > prevNotif.current;

        const chatIncreased =
          prevChat.current !== null && unreadChat > prevChat.current;

        if (notificationIncreased || chatIncreased) {
          playSound(user?.notification_sound || "ping");
        }

        prevNotif.current = unreadNotifications;
        prevChat.current = unreadChat;

        setUnread(unreadNotifications);
        setChatUnread(unreadChat);
      } catch {
        // Bildirim isteği başarısız olursa menü çalışmaya devam eder.
      }
    };

    load();

    const timer = setInterval(load, 15000);

    const handleUpdate = () => {
      load();
    };

    window.addEventListener("notifications-updated", handleUpdate);
    window.addEventListener("chat-updated", handleUpdate);

    return () => {
      clearInterval(timer);
      window.removeEventListener("notifications-updated", handleUpdate);
      window.removeEventListener("chat-updated", handleUpdate);
    };
  }, [user]);

  useEffect(() => {
    localStorage.setItem(
      "ofisgorev-sidebar-collapsed",
      desktopCollapsed ? "1" : "0"
    );
  }, [desktopCollapsed]);

  useEffect(() => {
    setMobileSidebarOpen(false);
    setMobileOrderOpen(false);
    setMobileMoreOpen(false);

    if (location.pathname.startsWith("/siparis-talep")) {
      setOrderMenuOpen(true);
    }
    if (location.pathname.startsWith("/gorevler") || location.pathname.startsWith("/tamamlanan")) {
      setTaskMenuOpen(true);
    }
    if (location.pathname.startsWith("/eticaret-kar")) {
      setProfitMenuOpen(true);
    }
    if (location.pathname.startsWith("/depo")) {
      setWarehouseMenuOpen(true);
    }
    if (location.pathname.startsWith("/pazaryeri-fiyat")) {
      setMarketplaceMenuOpen(true);
    }
    if (location.pathname.startsWith("/document")) {
      setDocumentMenuOpen(true);
    }
    if (location.pathname.startsWith("/calisanlar")) {
      setEmployeeMenuOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const clearHideTimer = () => {
      if (scrollUpHideTimer.current) {
        window.clearTimeout(scrollUpHideTimer.current);
        scrollUpHideTimer.current = null;
      }
    };

    const scheduleHide = () => {
      clearHideTimer();
      scrollUpHideTimer.current = window.setTimeout(() => {
        setScrollUpVisible(false);
      }, 3000);
    };

    const handleScroll = () => {
      const currentY = window.scrollY || document.documentElement.scrollTop || 0;
      const movingDown = currentY > lastScrollY.current;

      if (currentY > 220 && movingDown) {
        setScrollUpVisible(true);
        scheduleHide();
      } else if (currentY <= 220) {
        setScrollUpVisible(false);
        clearHideTimer();
      } else if (scrollUpVisible) {
        scheduleHide();
      }

      lastScrollY.current = Math.max(0, currentY);
    };

    lastScrollY.current = window.scrollY || document.documentElement.scrollTop || 0;
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearHideTimer();
    };
  }, [scrollUpVisible]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollUpVisible(false);
    if (scrollUpHideTimer.current) {
      window.clearTimeout(scrollUpHideTimer.current);
      scrollUpHideTimer.current = null;
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/giris");
  };

  const avatar = user?.avatar
    ? user.avatar.startsWith("http")
      ? user.avatar
      : `${BACKEND}${user.avatar}`
    : "";

  const pageTitle = getPageTitle(location.pathname);

  return (
    <div className="min-h-screen flex bg-background">
      <DesktopSidebar
        nav={nav}
        user={user}
        avatar={avatar}
        dark={dark}
        setDark={setDark}
        doLogout={doLogout}
        taskMenuOpen={taskMenuOpen}
        setTaskMenuOpen={setTaskMenuOpen}
        profitMenuOpen={profitMenuOpen}
        setProfitMenuOpen={setProfitMenuOpen}
        warehouseMenuOpen={warehouseMenuOpen}
        setWarehouseMenuOpen={setWarehouseMenuOpen}
        b2bMenuOpen={b2bMenuOpen}
        setB2bMenuOpen={setB2bMenuOpen}
        orderMenuOpen={orderMenuOpen}
        setOrderMenuOpen={setOrderMenuOpen}
        marketplaceMenuOpen={marketplaceMenuOpen}
        setMarketplaceMenuOpen={setMarketplaceMenuOpen}
        documentMenuOpen={documentMenuOpen}
        setDocumentMenuOpen={setDocumentMenuOpen}
        employeeMenuOpen={employeeMenuOpen}
        setEmployeeMenuOpen={setEmployeeMenuOpen}
        collapsed={desktopCollapsed}
        setCollapsed={setDesktopCollapsed}
        hasPermission={hasPermission}
      />

      <main className={`flex-1 pb-20 md:pb-0 relative min-w-0 transition-[margin] duration-200 ${desktopCollapsed ? "md:ml-16" : "md:ml-72"}`}>
        <MobileTopBar
          pageTitle={pageTitle}
          onMenuClick={() => setMobileSidebarOpen(true)}
        />
        <Outlet />
      </main>

      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Sayfanın başına dön"
        title="Yukarı çık"
        className={`fixed right-4 md:right-6 z-[45] w-11 h-11 md:w-12 md:h-12 rounded-2xl border border-blue-200/80 bg-white/90 dark:bg-slate-900/90 text-blue-600 dark:text-blue-300 shadow-lg shadow-blue-500/10 backdrop-blur-md flex items-center justify-center transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:border-blue-300 active:scale-95 bottom-[84px] md:bottom-6 ${
          scrollUpVisible
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-3 pointer-events-none"
        }`}
      >
        <span className="absolute inset-1 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/50 dark:to-indigo-950/40" />
        <ArrowUp className="relative z-10 w-5 h-5" strokeWidth={2.4} />
      </button>

      <MobileBottomNav
        unread={unread}
        chatUnread={chatUnread}
        location={location}
        hasPermission={hasPermission}
        onOrderClick={() => {
          setMobileMoreOpen(false);
          setMobileOrderOpen(true);
        }}
        onMoreClick={() => {
          setMobileOrderOpen(false);
          setMobileMoreOpen(true);
        }}
      />

      {mobileOrderOpen && (
        <MobileSheet
          title="Sipariş Talep"
          onClose={() => setMobileOrderOpen(false)}
        >
          <div className="grid grid-cols-2 gap-3">
            {hasPermission("order_requests") && <SheetLink
              to="/siparis-talep/urunler"
              icon={Boxes}
              label="Ürün Yönetimi"
            />}
            {hasPermission("order_requests") && <SheetLink
              to="/siparis-talep/yeni"
              icon={ShoppingCart}
              label="Yeni Sipariş"
            />}
            {hasPermission("order_requests") && <SheetLink
              to="/siparis-talep/siparisler"
              icon={ClipboardList}
              label="Siparişler"
            />}
            {hasPermission("order_requests") && <SheetLink
              to="/siparis-talep/tedarikciler"
              icon={Building2}
              label="Tedarikçiler"
            />}
            {hasPermission("order_requests", "edit") && (
              <SheetLink to="/siparis-talep/firma-ayarlari" icon={Settings} label="Firma Ayarları" />
            )}
          </div>
        </MobileSheet>
      )}

      {mobileMoreOpen && (
        <MobileSheet
          title="Diğer Menüler"
          onClose={() => setMobileMoreOpen(false)}
        >
          <div className="grid grid-cols-3 gap-3">
            {hasPermission("tasks") && <SheetLink to="/tamamlanan" icon={CheckCircle2} label="Tamamlanan" />}

            {hasPermission("ecommerce_profit") && <SheetLink to="/eticaret-kar" icon={Calculator} label="Kâr Hesapla" />}

            {hasPermission("ecommerce_profit") && <SheetLink to="/eticaret-kar/maliyetler" icon={Package} label="Ürün Maliyet" />}
            {hasPermission("warehouse_stock") && <SheetLink to="/depo/stok" icon={Boxes} label="Depo / Stok" />}
            {hasPermission("ecommerce_profit") && <SheetLink to="/eticaret-kar/kargo-maliyetleri" icon={Truck} label="Kargo Maliyetleri" />}

            {hasPermission("users") && <SheetLink to="/kullanicilar" icon={Users} label="Kullanıcılar" />}

            <SheetLink
              to="/profil"
              icon={UserCircle}
              label="Profil"
              avatar={avatar}
            />

            <button
              type="button"
              onClick={() => setDark(!dark)}
              className="min-h-[92px] rounded-xl border border-border bg-muted/25 flex flex-col items-center justify-center gap-2 text-[13px] font-medium active:scale-[0.98] transition"
            >
              {dark ? (
                <Sun className="w-[22px] h-[22px] shrink-0" />
              ) : (
                <Moon className="w-[22px] h-[22px] shrink-0" />
              )}
              <span>{dark ? "Açık Tema" : "Koyu Tema"}</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setMobileMoreOpen(false);
                setMobileSidebarOpen(true);
              }}
              className="min-h-[92px] rounded-xl border border-border bg-muted/25 flex flex-col items-center justify-center gap-2 text-[13px] font-medium active:scale-[0.98] transition"
            >
              <Menu className="w-5 h-5" />
              <span>Sol Menü</span>
            </button>

            <button
              type="button"
              onClick={doLogout}
              className="min-h-[92px] rounded-xl border border-border bg-muted/25 flex flex-col items-center justify-center gap-2 text-[13px] font-medium text-destructive active:scale-[0.98] transition"
            >
              <LogOut className="w-[22px] h-[22px] shrink-0" />
              <span>Çıkış</span>
            </button>
          </div>
        </MobileSheet>
      )}

      {mobileSidebarOpen && (
        <MobileSidebar
          nav={nav}
          user={user}
          avatar={avatar}
          dark={dark}
          setDark={setDark}
          doLogout={doLogout}
          taskMenuOpen={taskMenuOpen}
          setTaskMenuOpen={setTaskMenuOpen}
          profitMenuOpen={profitMenuOpen}
          setProfitMenuOpen={setProfitMenuOpen}
          warehouseMenuOpen={warehouseMenuOpen}
          setWarehouseMenuOpen={setWarehouseMenuOpen}
          b2bMenuOpen={b2bMenuOpen}
          setB2bMenuOpen={setB2bMenuOpen}
          orderMenuOpen={orderMenuOpen}
          setOrderMenuOpen={setOrderMenuOpen}
          marketplaceMenuOpen={marketplaceMenuOpen}
        setMarketplaceMenuOpen={setMarketplaceMenuOpen}
        documentMenuOpen={documentMenuOpen}
          setDocumentMenuOpen={setDocumentMenuOpen}
          employeeMenuOpen={employeeMenuOpen}
          setEmployeeMenuOpen={setEmployeeMenuOpen}
          onClose={() => setMobileSidebarOpen(false)}
          hasPermission={hasPermission}
        />
      )}
    </div>
  );
}

function DesktopSidebar({
  nav,
  user,
  avatar,
  dark,
  setDark,
  doLogout,
  taskMenuOpen,
  setTaskMenuOpen,
  profitMenuOpen,
  setProfitMenuOpen,
  warehouseMenuOpen,
  setWarehouseMenuOpen,
  b2bMenuOpen,
  setB2bMenuOpen,
  orderMenuOpen,
  setOrderMenuOpen,
  marketplaceMenuOpen,
  setMarketplaceMenuOpen,
  documentMenuOpen,
  setDocumentMenuOpen,
  employeeMenuOpen,
  setEmployeeMenuOpen,
  collapsed,
  setCollapsed,
  hasPermission,
}) {
  return (
    <aside className={`${collapsed ? "w-16" : "w-72"} border-r border-border bg-card hidden md:flex flex-col fixed h-screen z-30 transition-[width] duration-200`}>
      <Brand collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        <StandardNav nav={nav.filter((item) => item.to === "/")} collapsed={collapsed} />

        {hasPermission("tasks") && <TaskMenu open={taskMenuOpen} setOpen={setTaskMenuOpen} collapsed={collapsed} hasPermission={hasPermission} />}

        <StandardNav nav={nav.filter((item) => item.to === "/bildirimler")} collapsed={collapsed} />

        {["warehouse_stock","warehouse_movements","warehouse_recipes","warehouse_returns","warehouse_counts","warehouse_assets","warehouse_settings","warehouse_reports"].some((m) => hasPermission(m)) && <WarehouseMenu open={warehouseMenuOpen} setOpen={setWarehouseMenuOpen} collapsed={collapsed} hasPermission={hasPermission} />}

        {hasPermission("ecommerce_profit") && <ProfitMenu open={profitMenuOpen} setOpen={setProfitMenuOpen} collapsed={collapsed} />}

        {hasPermission("marketplace_price") && <MarketplacePriceMenu open={marketplaceMenuOpen} setOpen={setMarketplaceMenuOpen} collapsed={collapsed} />}

        <StandardNav nav={[
          ...(hasPermission("profit_loss") ? [{ to: "/kar-zarar", label: "Kâr / Zarar", icon: BarChart3 }] : []),
          ...(hasPermission("financial_archive") ? [{ to: "/finansal-arsiv", label: "Finansal Arşiv", icon: BookOpen }, { to: "/muhasebe-kontrol", label: "Muhasebe Kontrol Merkezi", icon: ClipboardCheck }] : []),
          ...(hasPermission("business_expenses") ? [{ to: "/isletme-giderleri", label: "İşletme Giderleri", icon: Receipt }] : []),
        ]} collapsed={collapsed} />

        {hasPermission("employees") && <EmployeeMenu open={employeeMenuOpen} setOpen={setEmployeeMenuOpen} collapsed={collapsed} />}

        {hasPermission("b2b") && <B2BMenu user={user} open={b2bMenuOpen} setOpen={setB2bMenuOpen} collapsed={collapsed} hasPermission={hasPermission} />}

        {hasPermission("order_requests") && <OrderMenu user={user} open={orderMenuOpen} setOpen={setOrderMenuOpen} collapsed={collapsed} hasPermission={hasPermission} />}

        {hasPermission("users") && <StandardNav nav={[{ to: "/kullanicilar", label: "Kullanıcılar", icon: Users }]} collapsed={collapsed} />}

        {hasPermission("documents") && <DocumentMenu open={documentMenuOpen} setOpen={setDocumentMenuOpen} collapsed={collapsed} />}
      </nav>

      <UserFooter
        user={user}
        avatar={avatar}
        dark={dark}
        setDark={setDark}
        doLogout={doLogout}
        collapsed={collapsed}
      />
    </aside>
  );
}

function MobileTopBar({ pageTitle, onMenuClick }) {
  return (
    <header className="md:hidden sticky top-0 z-30 h-14 bg-background/95 backdrop-blur-xl border-b border-border px-3 flex items-center gap-3">
      <button
        type="button"
        onClick={onMenuClick}
        className="w-10 h-10 rounded-lg border border-border bg-card shadow-sm inline-flex items-center justify-center"
        aria-label="Menüyü aç"
      >
        <Menu className="w-[22px] h-[22px] shrink-0" />
      </button>

      <div className="font-head font-bold text-lg truncate">{pageTitle}</div>
    </header>
  );
}

function MobileBottomNav({
  unread,
  chatUnread,
  location,
  hasPermission,
  onOrderClick,
  onMoreClick,
}) {
  const orderActive = location.pathname.startsWith("/siparis-talep");

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 bg-card/95 backdrop-blur-xl border-t border-border z-40 px-1"
      style={{
        height: "calc(72px + env(safe-area-inset-bottom))",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="h-[68px] grid grid-flow-col auto-cols-fr items-stretch">
        {hasPermission("dashboard") && <div className="h-full min-w-0"><BottomLink to="/" end icon={LayoutDashboard} label="Panel" /></div>}
        {hasPermission("tasks") && <div className="h-full min-w-0"><BottomLink to="/gorevler" icon={CheckSquare} label="Görevler" /></div>}

        {hasPermission("order_requests") && <button
          type="button"
          onClick={onOrderClick}
          className={`relative h-full min-w-0 px-0.5 flex flex-col items-center justify-center gap-0.5 text-[10px] leading-tight font-medium ${
            orderActive ? "text-primary" : "text-muted-foreground"
          }`}
        >
          <Package className="w-[22px] h-[22px] shrink-0" />
          <span className="whitespace-nowrap">Sipariş</span>
        </button>}

        {hasPermission("notifications") && <div className="h-full min-w-0"><BottomLink to="/sohbet" icon={MessageCircle} label="Sohbet" badge={chatUnread} /></div>}
        {hasPermission("notifications") && <div className="h-full min-w-0"><BottomLink to="/bildirimler" icon={Bell} label="Bildirimler" badge={unread} /></div>}

        <button
          type="button"
          onClick={onMoreClick}
          className="h-full min-w-0 px-0.5 flex flex-col items-center justify-center gap-0.5 text-[10px] leading-tight font-medium text-muted-foreground"
        >
          <MoreHorizontal className="w-[22px] h-[22px] shrink-0" />
          <span className="whitespace-nowrap">Menü</span>
        </button>
      </div>
    </nav>
  );
}

function BottomLink({ to, end, icon: Icon, label, badge = 0 }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `relative h-full w-full min-w-0 px-0.5 flex flex-col items-center justify-center gap-0.5 text-[10px] leading-tight font-medium ${
          isActive ? "text-primary" : "text-muted-foreground"
        }`
      }
    >
      <Icon className="w-[22px] h-[22px] shrink-0" />
      <span className="whitespace-nowrap">{label}</span>

      {badge > 0 && (
        <span className="absolute top-1 right-[18%] min-w-4 h-4 px-1 rounded-full bg-destructive text-white text-[9px] inline-flex items-center justify-center">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function MobileSheet({ title, onClose, children }) {
  return (
    <div className="md:hidden fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 bg-black/35"
        onClick={onClose}
        aria-label="Kapat"
      />

      <section
        className="absolute inset-x-3 bg-card border border-border rounded-[24px] shadow-2xl p-4"
        style={{
          bottom: "calc(80px + env(safe-area-inset-bottom))",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full inline-flex items-center justify-center hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {children}
      </section>
    </div>
  );
}

function SheetLink({ to, icon: Icon, label, avatar }) {
  return (
    <NavLink
      to={to}
      className="min-h-[92px] rounded-xl border border-border bg-muted/25 flex flex-col items-center justify-center gap-2 text-[13px] font-medium active:scale-[0.98] transition"
    >
      {avatar ? (
        <span className="w-9 h-9 rounded-full overflow-hidden border border-border">
          <img src={avatar} alt="" className="w-full h-full object-cover" />
        </span>
      ) : (
        <Icon className="w-6 h-6 text-primary" />
      )}
      <span className="whitespace-nowrap">{label}</span>
    </NavLink>
  );
}

function MobileSidebar({
  nav,
  user,
  avatar,
  dark,
  setDark,
  doLogout,
  taskMenuOpen,
  setTaskMenuOpen,
  profitMenuOpen,
  setProfitMenuOpen,
  warehouseMenuOpen,
  setWarehouseMenuOpen,
  b2bMenuOpen,
  setB2bMenuOpen,
  orderMenuOpen,
  setOrderMenuOpen,
  marketplaceMenuOpen,
  setMarketplaceMenuOpen,
  documentMenuOpen,
  setDocumentMenuOpen,
  employeeMenuOpen,
  setEmployeeMenuOpen,
  onClose,
  hasPermission,
}) {
  return (
    <div className="md:hidden fixed inset-0 z-[60]">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-label="Menüyü kapat"
      />

      <aside
        className="absolute inset-y-0 left-0 w-[76vw] max-w-[310px] bg-card border-r border-border shadow-2xl flex flex-col"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="h-16 px-4 flex items-center justify-between border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <CheckSquare className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-head font-bold text-xl">OfisGörev</span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full inline-flex items-center justify-center hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          <StandardNav nav={nav.filter((item) => item.to === "/")} mobile />
          {hasPermission("tasks") && <TaskMenu open={taskMenuOpen} setOpen={setTaskMenuOpen} mobile hasPermission={hasPermission} />}
          <StandardNav nav={nav.filter((item) => item.to === "/bildirimler")} mobile />
          {["warehouse_stock","warehouse_movements","warehouse_recipes","warehouse_returns","warehouse_counts","warehouse_assets","warehouse_settings","warehouse_reports"].some((m) => hasPermission(m)) && <WarehouseMenu open={warehouseMenuOpen} setOpen={setWarehouseMenuOpen} mobile hasPermission={hasPermission} />}
          {hasPermission("ecommerce_profit") && <ProfitMenu open={profitMenuOpen} setOpen={setProfitMenuOpen} mobile />}
          {hasPermission("marketplace_price") && <MarketplacePriceMenu open={marketplaceMenuOpen} setOpen={setMarketplaceMenuOpen} mobile />}

          <StandardNav nav={[...(hasPermission("profit_loss") ? [{to:"/kar-zarar",label:"Kâr / Zarar",icon:BarChart3}] : []), ...(hasPermission("financial_archive") ? [{to:"/finansal-arsiv",label:"Finansal Arşiv",icon:BookOpen}] : []), ...(hasPermission("accounting_control") ? [{to:"/muhasebe-kontrol",label:"Muhasebe Kontrol Merkezi",icon:ClipboardCheck}] : []), ...(hasPermission("business_expenses") ? [{to:"/isletme-giderleri",label:"İşletme Giderleri",icon:Receipt}] : [])]} mobile />

          {hasPermission("employees") && <EmployeeMenu open={employeeMenuOpen} setOpen={setEmployeeMenuOpen} mobile />}

          {hasPermission("b2b") && <B2BMenu user={user} open={b2bMenuOpen} setOpen={setB2bMenuOpen} mobile hasPermission={hasPermission} />}

          {hasPermission("order_requests") && <OrderMenu user={user} open={orderMenuOpen} setOpen={setOrderMenuOpen} mobile hasPermission={hasPermission} />}

          {hasPermission("users") && <StandardNav nav={[{ to: "/kullanicilar", label: "Kullanıcılar", icon: Users }]} mobile />}

          {hasPermission("documents") && <DocumentMenu open={documentMenuOpen} setOpen={setDocumentMenuOpen} mobile />}
        </nav>

        <UserFooter
          user={user}
          avatar={avatar}
          dark={dark}
          setDark={setDark}
          doLogout={doLogout}
          mobile
        />
      </aside>
    </div>
  );
}

function StandardNav({ nav, mobile = false, collapsed = false }) {
  return (
    <>
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          title={collapsed && !mobile ? item.label : undefined}
          className={({ isActive }) =>
            `flex items-center rounded-lg font-medium transition-colors ${
              collapsed && !mobile
                ? "justify-center px-0 py-2.5"
                : mobile
                  ? "gap-3 text-sm px-3 py-2.5"
                  : "gap-3 text-sm"
            } ${
              item.sub
                ? mobile
                  ? "pl-7"
                  : collapsed
                    ? ""
                    : "ml-5 pl-3 pr-3 py-2 border-l border-border"
                : mobile || collapsed
                  ? ""
                  : "px-3 py-2.5"
            } ${
              isActive
                ? item.sub
                  ? "text-primary bg-primary/10"
                  : "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            }`
          }
        >
          <item.icon className={mobile ? "w-[18px] h-[18px]" : "w-4 h-4"} />
          {(!collapsed || mobile) && <span>{item.label}</span>}

          {item.badge > 0 && (
            <span className={`${collapsed && !mobile ? "absolute translate-x-3 -translate-y-3 min-w-4 h-4 px-1 text-[9px] flex items-center justify-center" : "ml-auto text-xs px-1.5 py-0.5"} bg-destructive text-white rounded-full`}>
              {item.badge}
            </span>
          )}
        </NavLink>
      ))}
    </>
  );
}

// OFISGOREV_DROPDOWN_MENUS_V1
function TaskMenu({ open, setOpen, mobile = false, collapsed = false, hasPermission = () => true }) {
  return (
    <div className="pt-1">
      <button type="button" onClick={() => setOpen((v) => !v)} title={collapsed && !mobile ? "Görevler" : undefined}
        className={`w-full flex items-center rounded-lg font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed && !mobile ? "justify-center px-0 py-2.5" : mobile ? "gap-3 text-sm px-3 py-2.5" : "gap-3 text-sm px-3 py-2.5"}`}>
        <CheckSquare className={mobile ? "w-[18px] h-[18px]" : "w-4 h-4"} />
        {(!collapsed || mobile) && <span>Görevler</span>}
        {(!collapsed || mobile) && (open ? <ChevronDown className="w-4 h-4 ml-auto"/> : <ChevronRight className="w-4 h-4 ml-auto"/>)}
      </button>
      {open && !collapsed && <div className="mt-1 ml-5 pl-3 border-l border-border space-y-1">
        <OrderLink to="/gorevler" icon={CheckSquare} label="Görev Listeleri" mobile={mobile}/>
        <OrderLink to="/tamamlanan" icon={CheckCircle2} label="Tamamlanan" mobile={mobile}/>
        <OrderLink to="/gorev-raporlari" icon={BarChart3} label="Görev Raporları" mobile={mobile}/>
        {hasPermission("notifications") && <OrderLink to="/sohbet" icon={MessageCircle} label="Sohbet" mobile={mobile}/>}
      </div>}
    </div>
  );
}

function ProfitMenu({ open, setOpen, mobile = false, collapsed = false }) {
  return (
    <div className="pt-1">
      <button type="button" onClick={() => setOpen((v) => !v)} title={collapsed && !mobile ? "E-Ticaret Kâr Hesapla" : undefined}
        className={`w-full flex items-center rounded-lg font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed && !mobile ? "justify-center px-0 py-2.5" : mobile ? "gap-3 text-sm px-3 py-2.5" : "gap-3 text-sm px-3 py-2.5"}`}>
        <Calculator className={mobile ? "w-[18px] h-[18px]" : "w-4 h-4"} />
        {(!collapsed || mobile) && <span>E-Ticaret Kâr Hesapla</span>}
        {(!collapsed || mobile) && (open ? <ChevronDown className="w-4 h-4 ml-auto"/> : <ChevronRight className="w-4 h-4 ml-auto"/>)}
      </button>
      {open && !collapsed && <div className="mt-1 ml-5 pl-3 border-l border-border space-y-1">
        <OrderLink to="/eticaret-kar" icon={Calculator} label="Kârlılık Analizi" mobile={mobile}/>
        <OrderLink to="/eticaret-kar/maliyetler" icon={Package} label="Ürün Maliyet" mobile={mobile}/>
        <OrderLink to="/eticaret-kar/diger-giderler" icon={Package} label="Diğer Giderler" mobile={mobile}/>
        <OrderLink to="/eticaret-kar/kargo-maliyetleri" icon={Truck} label="Kargo Maliyetleri" mobile={mobile}/>
        <OrderLink to="/eticaret-kar/perakende" icon={Calculator} label="Perakende Fiyatlar" mobile={mobile}/>
        <OrderLink to="/eticaret-kar/toptan" icon={Calculator} label="Toptan Fiyatlar" mobile={mobile}/>
        <OrderLink to="/eticaret-kar/hizli-teklif" icon={Calculator} label="Hızlı Teklif Oluştur" mobile={mobile}/>
      </div>}
    </div>
  );
}

function EmployeeMenu({ open, setOpen, mobile = false, collapsed = false }) {
  return (
    <div className="pt-1">
      <button type="button" onClick={() => setOpen((v) => !v)} title={collapsed && !mobile ? "Çalışanlar" : undefined}
        className={`w-full flex items-center rounded-lg font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed && !mobile ? "justify-center px-0 py-2.5" : "gap-3 text-sm px-3 py-2.5"}`}>
        <Users className={mobile ? "w-[18px] h-[18px]" : "w-4 h-4"} />
        {(!collapsed || mobile) && <span>Çalışanlar</span>}
        {(!collapsed || mobile) && (open ? <ChevronDown className="w-4 h-4 ml-auto"/> : <ChevronRight className="w-4 h-4 ml-auto"/>)}
      </button>
      {open && !collapsed && <div className="mt-1 ml-5 pl-3 border-l border-border space-y-1">
        <OrderLink to="/calisanlar/personeller" icon={Users} label="Personeller" mobile={mobile}/>
        <OrderLink to="/calisanlar/izinler" icon={CalendarDays} label="Yıllık İzin" mobile={mobile}/>
        <OrderLink to="/calisanlar/ek-odemeler" icon={Banknote} label="Ek Ödemeler" mobile={mobile}/>
        <OrderLink to="/calisanlar/maas" icon={Calculator} label="Maaş Hesapla" mobile={mobile}/>
        <OrderLink to="/calisanlar/ozluk" icon={FolderOpen} label="Özlük" mobile={mobile}/>
      </div>}
    </div>
  );
}

function WarehouseMenu({ open, setOpen, mobile = false, collapsed = false, hasPermission = () => true }) {
  return (
    <div className="pt-1">
      <button type="button" onClick={() => setOpen((v) => !v)} title={collapsed && !mobile ? "Depo" : undefined}
        className={`w-full flex items-center rounded-lg font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed && !mobile ? "justify-center px-0 py-2.5" : mobile ? "gap-3 text-sm px-3 py-2.5" : "gap-3 text-sm px-3 py-2.5"}`}>
        <Boxes className={mobile ? "w-[18px] h-[18px]" : "w-4 h-4"} />
        {(!collapsed || mobile) && <span>Depo</span>}
        {(!collapsed || mobile) && (open ? <ChevronDown className="w-4 h-4 ml-auto"/> : <ChevronRight className="w-4 h-4 ml-auto"/>)}
      </button>
      {open && !collapsed && <div className="mt-1 ml-5 pl-3 border-l border-border space-y-1">
        {hasPermission("warehouse_stock") && <OrderLink to="/depo/stok" icon={Boxes} label="Stok" mobile={mobile}/>}
        {hasPermission("warehouse_movements") && <OrderLink to="/depo/stok-hareketleri" icon={ClipboardList} label="Stok Hareketleri" mobile={mobile}/>}
        {hasPermission("warehouse_recipes") && <OrderLink to="/depo/receteler" icon={ClipboardCheck} label="Reçeteler / Paket Ürünler" mobile={mobile}/>}
        {hasPermission("warehouse_returns") && <OrderLink to="/depo/iadeler" icon={ArrowUp} label="İadeler / Hasarlı Ürünler" mobile={mobile}/>}
        {hasPermission("warehouse_counts") && <OrderLink to="/depo/stok-sayim" icon={ScanLine} label="Stok Sayım" mobile={mobile}/>} 
        {hasPermission("warehouse_assets") && <OrderLink to="/depo/demirbaslar" icon={Boxes} label="Demirbaşlar" mobile={mobile}/>}
        {hasPermission("warehouse_settings") && <OrderLink to="/depo/barkod-ayarlari" icon={Settings} label="Barkod Ayarları" mobile={mobile}/>}
        {hasPermission("warehouse_reports") && <OrderLink to="/depo/raporlar" icon={Calculator} label="Raporlar" mobile={mobile}/>}
      </div>}
    </div>
  );
}

function B2BMenu({ user, open, setOpen, mobile = false, collapsed = false, hasPermission = () => true }) {
  const items = [
    ["/b2b", LayoutDashboard, "B2B Panel"],
    ["/b2b/firma-avcisi", Search, "Firma Avcısı"],
    ["/b2b/potansiyel-musteriler", Target, "Potansiyel Müşteriler"],
    ["/b2b/email-merkezi", Bell, "E-Mail Merkezi"],
    ["/b2b/arama-takibi", ClipboardList, "Arama Takibi"],
    ["/b2b/whatsapp-takibi", MessageCircle, "WhatsApp Takibi"],
    ["/b2b/teklifler", Receipt, "Teklifler"],
    ["/b2b/musteriler", Users, "Müşteriler"],
    ["/b2b/musteri-takip-plani", ClipboardList, "Müşteri Takip Planı"],
    ["/b2b/raporlar", Calculator, "Satış Raporları"],
  ];
  if (hasPermission("b2b", "edit")) items.push(["/b2b/ayarlar", Settings, "B2B Ayarları"]);
  return <div className="pt-1">
    <button type="button" onClick={() => setOpen(v => !v)} title={collapsed && !mobile ? "B2B Satış" : undefined}
      className={`w-full flex items-center rounded-lg font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed && !mobile ? "justify-center px-0 py-2.5" : mobile ? "gap-3 text-sm px-3 py-2.5" : "gap-3 text-sm px-3 py-2.5"}`}>
      <BriefcaseBusiness className={mobile ? "w-[18px] h-[18px]" : "w-4 h-4"}/>
      {(!collapsed || mobile) && <span>B2B Satış</span>}
      {(!collapsed || mobile) && (open ? <ChevronDown className="w-4 h-4 ml-auto"/> : <ChevronRight className="w-4 h-4 ml-auto"/>)}
    </button>
    {open && !collapsed && <div className="mt-1 ml-5 pl-3 border-l border-border space-y-1">
      {items.map(([to,icon,label]) => <OrderLink key={to} to={to} icon={icon} label={label} mobile={mobile}/>) }
    </div>}
  </div>;
}

function MarketplacePriceMenu({ open, setOpen, mobile = false, collapsed = false }) {
  return <div className="pt-1">
    <button type="button" onClick={() => setOpen(v => !v)} title={collapsed && !mobile ? "Pazaryeri Fiyat Yönetimi" : undefined}
      className={`w-full flex items-center rounded-lg font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed && !mobile ? "justify-center px-0 py-2.5" : "gap-3 text-sm px-3 py-2.5"}`}>
      <ShoppingCart className={mobile ? "w-[18px] h-[18px]" : "w-4 h-4"}/>
      {(!collapsed || mobile) && <span>Pazaryeri Fiyat Yönetimi</span>}
      {(!collapsed || mobile) && (open ? <ChevronDown className="w-4 h-4 ml-auto"/> : <ChevronRight className="w-4 h-4 ml-auto"/>)}
    </button>
    {open && !collapsed && <div className="mt-1 ml-5 pl-3 border-l border-border space-y-1">
      <OrderLink to="/pazaryeri-fiyat" icon={LayoutDashboard} label="Genel Bakış" mobile={mobile}/>
      <OrderLink to="/pazaryeri-fiyat/entegrasyonlar" icon={Settings} label="Entegrasyonlar" mobile={mobile}/>
      <OrderLink to="/pazaryeri-fiyat/urunler" icon={PackageOpen} label="Ürünler" mobile={mobile}/>
      <OrderLink to="/pazaryeri-fiyat/islemler" icon={ClipboardList} label="Güncelleme İşlemleri" mobile={mobile}/>
    </div>}
  </div>;
}

function DocumentMenu({ open, setOpen, mobile = false, collapsed = false }) {
  return (
    <div className="pt-1">
      <button type="button" onClick={() => setOpen((v) => !v)} title={collapsed && !mobile ? "Document" : undefined}
        className={`w-full flex items-center rounded-lg font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${collapsed && !mobile ? "justify-center px-0 py-2.5" : mobile ? "gap-3 text-sm px-3 py-2.5" : "gap-3 text-sm px-3 py-2.5"}`}>
        <BookOpen className={mobile ? "w-[18px] h-[18px]" : "w-4 h-4"} />
        {(!collapsed || mobile) && <span>Document</span>}
        {(!collapsed || mobile) && (open ? <ChevronDown className="w-4 h-4 ml-auto"/> : <ChevronRight className="w-4 h-4 ml-auto"/>)}
      </button>
      {open && !collapsed && <div className="mt-1 ml-5 pl-3 border-l border-border space-y-1">
        <OrderLink to="/document/kullanim-kilavuzu" icon={BookOpen} label="Kullanım Kılavuzu" mobile={mobile}/>
        <OrderLink to="/document/belgeler" icon={FolderOpen} label="Belgeler" mobile={mobile}/>
      </div>}
    </div>
  );
}

function OrderMenu({ user, open, setOpen, mobile = false, collapsed = false, hasPermission = () => true }) {
  return (
    <div className="pt-2">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        title={collapsed && !mobile ? "Sipariş Talep" : undefined}
        className={`w-full flex items-center rounded-lg font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors ${
          collapsed && !mobile
            ? "justify-center px-0 py-2.5"
            : mobile
              ? "gap-3 text-sm px-3 py-2.5"
              : "gap-3 text-sm px-3 py-2.5"
        }`}
      >
        <Package className={mobile ? "w-[18px] h-[18px]" : "w-4 h-4"} />
        {(!collapsed || mobile) && <span>Sipariş Talep</span>}
        {(!collapsed || mobile) && (open ? (
          <ChevronDown className="w-4 h-4 ml-auto" />
        ) : (
          <ChevronRight className="w-4 h-4 ml-auto" />
        ))}
      </button>

      {open && !collapsed && (
        <div className="mt-1 ml-5 pl-3 border-l border-border space-y-1">
          <OrderLink
            to="/siparis-talep/urunler"
            icon={Boxes}
            label="Ürün Yönetimi"
            mobile={mobile}
          />
          <OrderLink
            to="/siparis-talep/yeni"
            icon={ShoppingCart}
            label="Yeni Sipariş"
            mobile={mobile}
          />
          <OrderLink
            to="/siparis-talep/siparisler"
            icon={ClipboardList}
            label="Siparişler"
            mobile={mobile}
          />
          <OrderLink
            to="/siparis-talep/tedarikciler"
            icon={Building2}
            label="Tedarikçiler"
            mobile={mobile}
          />
          {hasPermission("order_requests", "edit") && (
            <OrderLink
              to="/siparis-talep/firma-ayarlari"
              icon={Settings}
              label="Firma Ayarları"
              mobile={mobile}
            />
          )}
        </div>
      )}
    </div>
  );
}

function OrderLink({ to, icon: Icon, label, mobile }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md font-medium transition-colors ${
          mobile ? "text-sm px-3 py-2.5" : "text-sm px-3 py-2"
        } ${
          isActive
            ? "text-primary bg-primary/10"
            : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`
      }
    >
      <Icon className={mobile ? "w-[18px] h-[18px] shrink-0" : "w-4 h-4 shrink-0"} />
      <span className="whitespace-nowrap">{label}</span>
    </NavLink>
  );
}

function Brand({ collapsed, onToggle }) {
  return (
    <div
      className={`h-16 border-b border-border flex items-center ${
        collapsed ? "justify-center px-2" : "justify-between px-4"
      }`}
    >
      {!collapsed && (
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center shrink-0">
            <CheckSquare className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-head font-bold text-lg tracking-tight truncate">
            OfisGörev
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={onToggle}
        title={collapsed ? "Menüyü aç" : "Menüyü küçült"}
        className="w-9 h-9 rounded-md border border-border bg-background inline-flex items-center justify-center hover:bg-accent transition-colors shrink-0"
      >
        <Menu className="w-5 h-5" />
      </button>
    </div>
  );
}

function UserFooter({
  user,
  avatar,
  dark,
  setDark,
  doLogout,
  mobile = false,
  collapsed = false,
}) {
  if (collapsed && !mobile) {
    return (
      <div className="p-2 border-t border-border space-y-2">
        <NavLink
          to="/profil"
          title="Profil"
          className="w-10 h-10 mx-auto rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold overflow-hidden hover:ring-2 hover:ring-primary/30"
        >
          {avatar ? (
            <img src={avatar} alt="" className="w-full h-full object-cover" />
          ) : (
            user?.name?.[0]?.toUpperCase()
          )}
        </NavLink>

        <Button
          variant="outline"
          size="icon"
          className="w-10 h-10 mx-auto flex"
          onClick={() => setDark(!dark)}
          title="Tema"
        >
          {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>

        <Button
          variant="outline"
          size="icon"
          className="w-10 h-10 mx-auto flex"
          onClick={doLogout}
          title="Çıkış"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <div className={`${mobile ? "p-3" : "p-4"} border-t border-border`}>
      <NavLink
        to="/profil"
        className="flex items-center gap-3 mb-2 px-1 py-1 rounded-md hover:bg-accent transition-colors"
      >
        <div
          className={`rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold overflow-hidden shrink-0 ${
            mobile ? "w-10 h-10 text-sm" : "w-9 h-9 text-sm"
          }`}
        >
          {avatar ? (
            <img src={avatar} alt="" className="w-full h-full object-cover" />
          ) : (
            user?.name?.[0]?.toUpperCase()
          )}
        </div>

        <div className="min-w-0">
          <div className="text-sm font-medium truncate flex items-center gap-1">
            {user?.name}
            {user?.role === "admin" && (
              <Shield className="w-3 h-3 text-primary" />
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {user?.email}
          </div>
        </div>
      </NavLink>

      <div className="flex gap-2">
        <Button
          variant="outline"
          size={mobile ? "default" : "sm"}
          className="flex-1"
          onClick={() => setDark(!dark)}
        >
          {dark ? (
            <Sun className="w-4 h-4 mr-2" />
          ) : (
            <Moon className="w-4 h-4 mr-2" />
          )}
          {mobile ? "Tema" : null}
        </Button>

        <Button
          variant="outline"
          size={mobile ? "default" : "sm"}
          className="flex-1"
          onClick={doLogout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          {mobile ? "Çıkış" : null}
        </Button>
      </div>
    </div>
  );
}

function getPageTitle(pathname) {
  if (pathname.startsWith("/document/kullanim-kilavuzu")) return "Kullanım Kılavuzu";
  if (pathname.startsWith("/document/belgeler")) return "Belgeler";
  if (pathname.startsWith("/depo/stok-hareketleri")) return "Stok Hareketleri";
  if (pathname.startsWith("/depo/receteler")) return "Depo Reçeteleri";
  if (pathname.startsWith("/depo/iadeler")) return "İadeler / Hasarlı Ürünler";
  if (pathname.startsWith("/depo/stok-sayim")) return "Stok Sayım";
  if (pathname.startsWith("/depo/demirbaslar")) return "Demirbaşlar";
  if (pathname.startsWith("/depo/barkod-ayarlari")) return "Barkod Ayarları";
  if (pathname.startsWith("/depo/raporlar")) return "Stok Raporları";
  if (pathname.startsWith("/depo/stok")) return "Stok";
  if (pathname.startsWith("/eticaret-kar/perakende")) return "Perakende Fiyatlar";
  if (pathname.startsWith("/eticaret-kar/toptan")) return "Toptan Fiyatlar";
  if (pathname.startsWith("/eticaret-kar/diger-giderler")) return "Diğer Giderler";
  if (pathname.startsWith("/eticaret-kar/maliyetler")) return "Ürün Maliyet";
  if (pathname.startsWith("/eticaret-kar")) return "E-Ticaret Kâr Hesapla";
  if (pathname === "/") return "Panel";
  if (pathname.startsWith("/gorevler")) return "Görevler";
  if (pathname.startsWith("/tamamlanan")) return "Tamamlanan";
  if (pathname.startsWith("/gorev-raporlari")) return "Görev Raporları";
  if (pathname.startsWith("/sohbet")) return "Sohbet";
  if (pathname.startsWith("/bildirimler")) return "Bildirimler";
  if (pathname.startsWith("/kullanicilar")) return "Kullanıcılar";
  if (pathname.startsWith("/kar-zarar")) return "Kâr / Zarar";
  if (pathname.startsWith("/finansal-arsiv")) return "Finansal Arşiv";
  if (pathname.startsWith("/muhasebe-kontrol")) return "Muhasebe Kontrol Merkezi";
  if (pathname.startsWith("/isletme-giderleri")) return "İşletme Giderleri";
  if (pathname.startsWith("/calisanlar/personeller")) return "Personeller";
  if (pathname.startsWith("/calisanlar/izinler")) return "Yıllık İzin";
  if (pathname.startsWith("/calisanlar/ek-odemeler")) return "Ek Ödemeler";
  if (pathname.startsWith("/calisanlar/maas")) return "Maaş Hesapla";
  if (pathname.startsWith("/calisanlar/ozluk")) return "Özlük Dosyası";
  if (pathname.startsWith("/profil")) return "Profil";
  if (pathname.startsWith("/siparis-talep/urunler")) return "Ürün Yönetimi";
  if (pathname.startsWith("/siparis-talep/yeni")) return "Yeni Sipariş";
  if (pathname.startsWith("/siparis-talep/siparisler")) return "Siparişler";
  if (pathname.startsWith("/siparis-talep/tedarikciler")) return "Tedarikçiler";
  if (pathname.startsWith("/siparis-talep/firma-ayarlari"))
    return "Firma Ayarları";
  return "OfisGörev";
}
